import api from './api';

export interface DashboardMetrics {
  activeRoadmaps: number;
  activeTransformations: number;
  activeRisks: number;
  inchstonesCompleted: number;
  inchstonesPlanned: number;
}

export interface InchstoneStats {
  completed: number;
  total: number;
  percentage: number;
}

export const dashboardService = {
  // Get dashboard metrics
  async getMetrics(): Promise<DashboardMetrics> {
    try {
      // Fetch all metrics in parallel
      const [roadmapsRes, transformationsRes, risksRes, inchstonesRes] = await Promise.all([
        this.getActiveRoadmaps(),
        this.getActiveTransformations(),
        this.getActiveRisks(),
        this.getInchstoneStats()
      ]);

      
      return {
        activeRoadmaps: roadmapsRes,
        activeTransformations: transformationsRes,
        activeRisks: risksRes,
        inchstonesCompleted: inchstonesRes.completed,
        inchstonesPlanned: inchstonesRes.total
      };
    } catch (error: any) {
      // Suppress timeout errors - they're expected for slow endpoints
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
      if (!isTimeoutError) {
        console.error('Error fetching dashboard metrics:', error);
      }
      return {
        activeRoadmaps: 0,
        activeTransformations: 0,
        activeRisks: 0,
        inchstonesCompleted: 0,
        inchstonesPlanned: 0
      };
    }
  },

  // Get active roadmaps count
  async getActiveRoadmaps(): Promise<number> {
    try {
      const response = await api.get('/tenant/roadmaps');
      return response.data?.data?.length || 0;
    } catch (error: any) {
      // Suppress timeout errors - they're expected for slow endpoints
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
      if (!isTimeoutError) {
        console.error('Error fetching roadmaps:', error);
      }
      return 0;
    }
  },

  // Get active transformations count
  async getActiveTransformations(): Promise<number> {
    try {
      const response = await api.get('/transformations');
      // Use total_count from pagination if available, otherwise fall back to array length
      return response.data?.pagination?.total_count || response.data?.data?.length || 0;
    } catch (error: any) {
      // Suppress timeout errors - they're expected for slow endpoints
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
      if (!isTimeoutError) {
        console.error('Error fetching transformations:', error);
      }
      return 0;
    }
  },

  // Get active risks count
  async getActiveRisks(): Promise<number> {
    try {
      const response = await api.get('/risks/dashboard');
      return response.data?.data?.summary?.total_risks || 0;
    } catch (error: any) {
      // Suppress timeout errors - they're expected for slow endpoints
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
      if (!isTimeoutError) {
        console.error('Error fetching risks:', error);
      }
      return 0;
    }
  },

  /**
   * Tenant-wide inchstone counts (all transformations). Uses the same aggregate as the PE portfolio
   * dashboard SQL (`InchstoneEntity.getOverallInchstoneStats`). The previous implementation only summed
   * stats for the first 10 transformations from `/transformations`, which could show 0/0 when inchstones
   * lived on other transformations.
   */
  async getInchstoneStats(): Promise<InchstoneStats> {
    try {
      const response = await api.get<{
        success?: boolean;
        data?: {
          total_inchstones: number;
          completed_inchstones: number;
          completion_percentage: number;
        };
      }>('/inchstones/stats');
      const d = response.data?.data;
      if (!d) {
        return { completed: 0, total: 0, percentage: 0 };
      }
      const total = Number(d.total_inchstones ?? 0);
      const completed = Number(d.completed_inchstones ?? 0);
      return {
        completed,
        total,
        percentage:
          typeof d.completion_percentage === 'number'
            ? d.completion_percentage
            : total > 0
              ? Math.round((completed / total) * 100)
              : 0,
      };
    } catch (error: any) {
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
      if (!isTimeoutError) {
        console.error('Error fetching inchstone stats:', error);
      }
      return {
        completed: 0,
        total: 0,
        percentage: 0,
      };
    }
  }
};
